import {
  Box,
  Container,
  Typography,
  useTheme,
  Fade,
} from "@mui/material";
import { useEffect, useState, useCallback } from "react";
import { useIncomesInRange } from "../../hooks/income";
import { useNavigate, useSearchParams } from "react-router-dom";
import { 
  LoginOutlined, 
  TrendingUp,
} from "@mui/icons-material";
import { getMonthsLongNames } from "../../utils/DateUtil";
import { useAuth } from "../../auth/AuthContext";
import { LOGIN_ROUTE_PATH } from "../../utils/constants/AppConstants";
import IncomeTableComponent from "../../components/finance/IncomeTableComponent";
import { PageContainer, ModernCard, GradientButton } from "../../components/common/StyledComponents";
import MonthYearPicker from "../../components/common/MonthYearPicker";
import { getMonthIndex, getFirstDayOfMonth, getLastDayOfMonth } from "../../utils/DateUtil";

const IncomePage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { isAuthenticated } = useAuth();
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonthIndex = today.getMonth();
  const monthNames = getMonthsLongNames();

  const [searchParams, setSearchParams] = useSearchParams();
  const urlYear = Number(searchParams.get("year")) || currentYear;
  const urlMonth = searchParams.get("month") || monthNames[currentMonthIndex];

  const [selectedYear, setSelectedYear] = useState(urlYear);
  const [selectedMonth, setSelectedMonth] = useState(urlMonth);
  const [, setIncomeCategories] = useState<{ [category: string]: number }>({});
  const [, setBudgetUpdateTrigger] = useState(0);
  const [dataKey, setDataKey] = useState(0); // For triggering transitions

  const updateSearchParams = useCallback((newParams: Record<string, string>) => {
    const updatedParams = new URLSearchParams(searchParams.toString());
    Object.entries(newParams).forEach(([key, value]) => {
      if (value) {
        updatedParams.set(key, value);
      } else {
        updatedParams.delete(key);
      }
    });
    setSearchParams(updatedParams);
  }, [searchParams, setSearchParams]);

  const handleYearChange = (year: number) => {
    setSelectedYear(year);
    updateSearchParams({ year: year.toString() });
    setDataKey(prev => prev + 1); // Trigger transition
  };

  const handleMonthChange = (month: string) => {
    setSelectedMonth(month);
    updateSearchParams({ month });
    setDataKey(prev => prev + 1); // Trigger transition
  };

  const startDate = getFirstDayOfMonth(selectedYear, getMonthIndex(selectedMonth));
  const endDate = getLastDayOfMonth(selectedYear, getMonthIndex(selectedMonth));
  
  const { data: incomes = [] } = useIncomesInRange(startDate, endDate);
  
  // Fetch income categories for budget overview
  const fetchIncomeCategories = useCallback(() => {
    if (!isAuthenticated) return;
    
    // Group incomes by category
    const categoriesMap: { [category: string]: number } = {};
    incomes.forEach(income => {
      categoriesMap[income.category] = (categoriesMap[income.category] || 0) + income.amount;
    });
    
    setIncomeCategories(categoriesMap);
  }, [incomes, isAuthenticated]);

  const handleBudgetUpdated = () => {
    setBudgetUpdateTrigger(prev => prev + 1);
  };

  useEffect(() => {
    fetchIncomeCategories();
  }, [fetchIncomeCategories]);

  useEffect(() => {
    if (isAuthenticated) {
      const needsInit = !searchParams.get("year") || !searchParams.get("month");
      if (needsInit) {
        updateSearchParams({
          year: selectedYear.toString(),
          month: selectedMonth,
        });
      }
    }
  }, [isAuthenticated, searchParams, selectedMonth, selectedYear, updateSearchParams]);

  if (!isAuthenticated) {
    return (
      <PageContainer>
        <Container maxWidth="lg" sx={{ py: 6, textAlign: 'center' }}>
          <Box sx={{ mb: 4 }}>
            <TrendingUp sx={{ fontSize: '4rem', color: theme.palette.success.main, mb: 2 }} />
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
              Track Your Incomes
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto', mb: 4 }}>
              Monitor all your revenue sources and income streams. Categorize earnings, track financial growth, and analyze your income patterns with detailed insights.
            </Typography>
            <GradientButton
              variant="contained"
              startIcon={<LoginOutlined />}
              onClick={() => navigate(LOGIN_ROUTE_PATH)}
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                px: 4,
                py: 1.5,
              }}
            >
              Sign In to Continue
            </GradientButton>
          </Box>
        </Container>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Fade in={true} timeout={400}>
          <Box>
            {/* Header */}
            <Box sx={{ mb: 4, textAlign: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 2 }}>
                <TrendingUp sx={{ fontSize: '2.5rem', color: theme.palette.success.main }} />
                <Typography 
                  variant="h3" 
                  sx={{ 
                    fontWeight: 700, 
                    background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  Income Tracker
                </Typography>
              </Box>
              <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
                Track and categorize your income for optimal financial growth
              </Typography>
            </Box>

            {/* Period Selector */}
            <Box sx={{ mb: 3 }}>
              <ModernCard sx={{ p: 2.5 }}>
                <MonthYearPicker
                  selectedMonth={selectedMonth}
                  selectedYear={selectedYear}
                  onMonthChange={handleMonthChange}
                  onYearChange={handleYearChange}
                  colorTheme="success"
                  transactionCount={incomes.length}
                  isLoading={false}
                />
              </ModernCard>
            </Box>

            {/* Budget Overview */}
            {/* {isAuthenticated && (
              <BudgetOverviewCard
                year={selectedYear}
                month={selectedMonth}
                isExpense={false}
                categories={incomeCategories}
                onBudgetUpdated={handleBudgetUpdated}
              />
            )} */}

            {/* Table Section with Animation */}
            <Fade in={true} timeout={600} key={dataKey}>
              <ModernCard>
                <IncomeTableComponent 
                  year={selectedYear} 
                  month={selectedMonth}
                  onIncomeChange={handleBudgetUpdated}
                />
              </ModernCard>
            </Fade>
          </Box>
        </Fade>
      </Container>
    </PageContainer>
  );
};

export default IncomePage;
